<template>
  <MapFormateursCanvas
    :formateurs="formateurs"
    :couleur-de="couleurSituation"
    :contenu-bulle="contenuBulle"
    :titre-de="nomAffiche"
    :highlighted-id="highlightedId"
    :centre="centre"
    :centre-titre="centre?.libelle || ''"
    :rayon-km="centre ? rayonKm : null"
    :note="note"
    @highlight="$emit('highlight', $event)"
    @bulle-ouverte="afficherTrajet"
  >
    <!-- La légende revient : la carte redistingue les enregistrés des
         inscriptions en cours, et la corbeille garde ses trois raisons de
         sortir de la liste. Des couleurs sans légende ne disent rien. -->
    <template #legende>
      <span v-for="item in legende" :key="item.label" class="legend-item">
        <span class="legend-dot" :style="{ background: item.couleur }"></span> {{ item.label }}
      </span>
    </template>
  </MapFormateursCanvas>
</template>

<script setup>
/**
 * La carte de la liste des formateurs : où sont-ils, et où n'y a-t-il personne.
 *
 * C'est la carte de la diffusion d'une mission, montée sans mission — même
 * socle (`MapFormateursCanvas`), donc mêmes positions, même fond départemental,
 * même dispersion des points qui se superposent. Ce que cet écran-ci change,
 * c'est le SENS DE LA COULEUR : sans mission, un point ne peut rien dire de sa
 * distance ni de son périmètre, alors il dit s'il est ENREGISTRÉ (vert) ou son
 * inscription EN COURS (orange). Deux couleurs, pas cinq : sur mille points, une
 * carte ne répond bien qu'à une seule question, et celle-ci est « puis-je
 * l'appeler ? ». La corbeille, elle, garde son nuancier.
 *
 * LE POINT EST POSÉ À LA COMMUNE, jamais à l'adresse — il vient du code postal,
 * résolu côté serveur. Le domicile d'un formateur n'a pas à être affiché.
 *
 * PAS DE SÉLECTION ICI. Sur la diffusion, cocher des points sert à leur envoyer
 * une proposition ; la liste des formateurs ne diffuse rien. Un clic ouvre donc
 * la bulle, et la bulle mène à la fiche.
 */
import { computed } from 'vue'
import MapFormateursCanvas from '../shared/MapFormateursCanvas.vue'
import { FORMATEUR_STATUT_BADGE_CONFIG } from '../../constants/formateur.js'
import { formateurDisplayName as nomAffiche } from '../../utils/formateurDisplay.js'
import { echapper, mentionDiscrete } from '../shared/map-bulle-formateur.js'
import { bulleFormateurListe } from './bulle-formateur-liste.js'
import { trajetRoutier } from './map-trajet.js'

const props = defineProps({
  formateurs: { type: Array, default: () => [] },
  /** Point de référence posé par l'équipe, autour duquel se trace le rayon. */
  centre: { type: Object, default: null },
  rayonKm: { type: Number, default: 50 },
  highlightedId: { type: Number, default: null },
  /** La légende ne montre que les situations que le périmètre courant peut contenir. */
  enCorbeille: { type: Boolean, default: false },
})

// Plus d'`ouvrir` : le lien de la bulle ouvre la fiche dans un nouvel onglet,
// le navigateur s'en charge et aucun parent n'écoutait plus cet événement.
defineEmits(['highlight'])

// Les teintes saturées des badges de statut : la carte emprunte la couleur de
// bordure, la seule des trois qui tienne sur un point de six pixels.
const COULEUR_STATUT = Object.fromEntries(
  Object.entries(FORMATEUR_STATUT_BADGE_CONFIG).map(([statut, config]) => [statut, config.border])
)

/**
 * DEUX COULEURS SUR LA CARTE ORDINAIRE, et deux seulement : le formateur est
 * enregistré (vert) ou son inscription est en cours (orange). C'est la seule
 * question qu'on se pose en regardant une France de points — puis-je
 * l'appeler ? — et un nuancier de cinq teintes la noyait.
 *
 * L'ORANGE EST CELUI DU BADGE « Inscription en cours » de la liste : la même
 * situation porte la même couleur d'une vue à l'autre (demande de l'équipe du
 * 29/09, le cyan d'avant ne se reliait à rien). Le point de référence et le
 * point cliqué sont orange aussi, mais s'en distinguent par la forme — une
 * flèche — et par la taille et le contour épais.
 */
const COULEUR_ENREGISTRE = COULEUR_STATUT.valide
const COULEUR_EN_COURS = COULEUR_STATUT.en_attente

const COULEUR_BLACKLISTE = '#ef4444'
const COULEUR_INACTIF = '#9ca3af'

/**
 * Ce qu'un point dit de lui-même, dans l'ORDRE où on le lit — le même ordre que
 * la colonne « Situation » de la corbeille (cf. FORMATEUR_POUBELLE_SITUATIONS).
 * Une fiche supprimée est supprimée, même si elle était inactive avant : la
 * peindre en gris « inactif » désignerait le mauvais geste.
 */
function couleurSituation(f) {
  if (f?.statut === 'corbeille') return COULEUR_STATUT.corbeille
  if (props.enCorbeille && f?.blackliste) return COULEUR_BLACKLISTE
  if (props.enCorbeille && f?.actif === false) return COULEUR_INACTIF
  if (f?.statut === 'valide') return COULEUR_ENREGISTRE
  return COULEUR_EN_COURS
}

const LEGENDE_LISTE = [
  { label: 'Enregistré', couleur: COULEUR_ENREGISTRE },
  { label: 'En cours d’inscription', couleur: COULEUR_EN_COURS },
]

// La corbeille garde son nuancier : ses quatre populations n'ont rien en commun
// sinon d'être sorties de la liste, et sans légende ses couleurs ne voudraient
// plus rien dire.
const LEGENDE_CORBEILLE = [
  { label: 'Supprimé', couleur: COULEUR_STATUT.corbeille },
  { label: 'Inactif', couleur: COULEUR_INACTIF },
  { label: 'Blacklisté', couleur: COULEUR_BLACKLISTE },
  // Les fiches de la campagne d'import restées sans suite (sans réponse,
  // injoignables) : toujours « invitées », donc peintes comme une inscription
  // en cours — la légende doit le dire.
  { label: 'Campagne sans suite', couleur: COULEUR_EN_COURS },
]

/**
 * NI LE POINT DE RÉFÉRENCE NI LE SURLIGNAGE N'Y FIGURENT. Le premier se
 * désigne tout seul — c'est le repère qu'on vient de poser soi-même, au centre
 * du cercle qu'on a tracé. Le second est un état d'un dixième de seconde, celui
 * du point qu'on est en train de cliquer : légender ce qu'on a sous le doigt,
 * c'est user deux lignes de légende à expliquer ce qui n'a jamais fait de doute.
 */
const legende = computed(() => (props.enCorbeille ? LEGENDE_CORBEILLE : LEGENDE_LISTE))

/**
 * LE SEUL BANDEAU QUE LA CARTE PORTE : la précision du point de référence. Elle
 * change ce que le cercle veut dire — trente kilomètres autour du centre d'un
 * département, ce n'est pas trente kilomètres autour d'une rue, et une distance
 * affichée au kilomètre près le ferait oublier.
 *
 * La carte comptait aussi les fiches sans code postal reconnu, qu'elle ne peut
 * pas placer. Retiré : la ligne s'affichait sur un fichier où le cas est rare,
 * elle occupait le coin de l'écran en permanence, et elle ne mène à aucun
 * geste — on ne corrige pas un code postal depuis une carte.
 */
const note = computed(() => {
  if (props.centre?.precision === 'departement') {
    return 'Point posé au centre du département : les distances sont des ordres de grandeur'
  }
  if (props.centre?.precision === 'commune') {
    return 'Point posé au centre de la commune : les distances sont approchées'
  }
  return null
})

/** La distance à vol d'oiseau, quand OSRM ne rend pas d'itinéraire : dite comme telle. */
function secoursVolDOiseau(f) {
  if (f.distanceKm == null) return mentionDiscrete('Trajet indisponible')
  return `${echapper(f.distanceKm)} km ${mentionDiscrete('à vol d’oiseau')}`
}

/**
 * Complète la bulle qui vient de s'ouvrir avec le trajet en voiture jusqu'au
 * point de référence. On cherche dans la racine de CETTE bulle, que le socle
 * transmet — pas dans le document, où la première bulle venue ferait l'affaire.
 * La réponse arrive parfois après qu'on a ouvert une autre bulle, ou refermé
 * celle-ci : si sa racine a quitté le document, on n'écrit rien.
 */
async function afficherTrajet(f, racine) {
  if (!props.centre || !f?.coords) return
  // `CSS.escape` et non le seul échappement HTML : ce sélecteur est du CSS.
  const selecteur = `[data-trajet="${CSS.escape(String(f.id))}"]`
  let html
  try {
    html = echapper(await trajetRoutier(f.coords, props.centre))
    // Parti du centre du département, le trajet n'est qu'un ordre de grandeur.
    if (f.coordsPrecision === 'departement') html += ` ${mentionDiscrete('(approché)')}`
  } catch {
    html = secoursVolDOiseau(f)
  }
  if (!racine?.isConnected) return
  const cible = racine.querySelector(selecteur)
  if (cible) cible.innerHTML = html
}

/** La bulle d'un point (cf. bulle-formateur-liste.js) : la ligne du trajet n'a de sens qu'autour d'un centre. */
function contenuBulle(f) {
  return bulleFormateurListe(f, { avecTrajet: Boolean(props.centre) })
}
</script>

